import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { extApi } from '../api/extensibilityClient';

export function VerificationLayerPage() {
  const [data, setData] = useState<any>(null);
  const [level, setLevel] = useState<'macro' | 'area' | 'indicator'>('macro');

  useEffect(() => { extApi.verification().then(setData).catch(console.error); }, []);

  if (!data) return <div className="loading">Loading verification layer…</div>;

  return (
    <div>
      <h1 className="page-title">Verification Layer</h1>
      <p className="page-subtitle">Official observed data vs baseline, selected scenario, and PM target — with freshness classification.</p>

      <div className="card" style={{ display: 'flex', gap: '0.5rem' }}>
        {(['macro', 'area', 'indicator'] as const).map((l) => (
          <button key={l} type="button" className={`btn ${level === l ? '' : 'secondary'}`} onClick={() => setLevel(l)}>
            Level {l === 'macro' ? '1 — Macro' : l === 'area' ? '2 — Policy area' : '3 — Indicator'}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', alignSelf: 'center' }}>
          Overall attainment: {data.overall_attainment_pct != null ? `${(data.overall_attainment_pct * 100).toFixed(0)}%` : '—'}
        </span>
      </div>

      {level === 'macro' && (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Indicator</th><th>Actual</th><th>Scenario</th><th>Target</th><th>Status</th><th>Freshness</th></tr></thead>
            <tbody>
              {data.macro.map((m: any) => (
                <tr key={m.variable_id}>
                  <td>{m.variable_name}</td><td>{m.actual_value ?? '—'}</td><td>{m.scenario_value ?? '—'}</td>
                  <td>{data.pm_target}%</td><td>{m.verification_status}</td><td>{m.freshness ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {level === 'area' && (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Policy area</th><th>Attainment</th><th>Indicators</th><th>Off-track</th></tr></thead>
            <tbody>
              {data.policy_areas.map((a: any) => (
                <tr key={a.policy_area}>
                  <td>{a.policy_area}</td>
                  <td>{a.attainment_pct != null ? `${(a.attainment_pct * 100).toFixed(0)}%` : '—'}</td>
                  <td>{a.indicator_count}</td><td>{a.off_track_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {level === 'indicator' && (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Variable</th><th>Actual</th><th>Scenario</th><th>Gap</th><th>Position</th><th>Status</th><th>Freshness</th></tr></thead>
            <tbody>
              {data.indicators.slice(0, 80).map((i: any) => (
                <tr key={i.variable_id}>
                  <td>{i.variable_name.slice(0, 30)}</td><td>{i.actual_value ?? '—'}</td><td>{i.scenario_value ?? '—'}</td>
                  <td>{i.absolute_gap != null ? i.absolute_gap.toFixed(2) : '—'}</td><td>{i.scenario_position}</td>
                  <td>{i.verification_status}</td><td>{i.freshness}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function GapDecompositionPanel() {
  const [data, setData] = useState<any>(null);
  useEffect(() => { extApi.gapDecomposition().then(setData).catch(console.error); }, []);
  if (!data) return null;
  return (
    <div className="card">
      <h3>Model-Implied GDP Gap Decomposition</h3>
      <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>{data.disclaimer}</p>
      <p>Macro gap: <strong>{data.macro_gap_pp?.toFixed(2)} pp</strong> to {data.pm_target}% target</p>
      <table className="data-table">
        <thead><tr><th>Policy area</th><th>Implied gap (pp)</th><th>Contribution</th></tr></thead>
        <tbody>
          {data.area_decomposition.map((a: any) => (
            <tr key={a.policy_area}><td>{a.policy_area}</td><td>{a.implied_gap_pp.toFixed(3)}</td><td>{a.contribution_pp.toFixed(3)} pp</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
